if (!customElements.get('lp-primer-video-reviews')) {
  customElements.define(
    'lp-primer-video-reviews',
    class LpPrimerVideoReviews extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-reviews-track]');
        this.slides = Array.from(this.querySelectorAll('[data-review-slide]'));
        this.previousButton = this.querySelector('[data-reviews-previous]');
        this.nextButton = this.querySelector('[data-reviews-next]');
        this.controlHandlers = [];

        this.onScroll = this.updateNavigation.bind(this);
        this.onPrevious = () => this.move(-1);
        this.onNext = () => this.move(1);
        this.onBlockSelect = this.handleBlockSelect.bind(this);

        this.previousButton?.addEventListener('click', this.onPrevious);
        this.nextButton?.addEventListener('click', this.onNext);
        this.track?.addEventListener('scroll', this.onScroll, { passive: true });
        this.addEventListener('shopify:block:select', this.onBlockSelect);

        this.setupVideoControls();

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
          if (this.track) this.resizeObserver.observe(this.track);
        }

        this.updateNavigation();
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.track?.removeEventListener('scroll', this.onScroll);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.controlHandlers.forEach(({ element, eventName, handler, options }) => {
          element.removeEventListener(eventName, handler, options);
        });
        this.resizeObserver?.disconnect();
      }

      setupVideoControls() {
        this.querySelectorAll('[data-video-media]').forEach((media) => {
          const video = media.querySelector('video');
          if (!video) return;

          video.controls = false;
          video.removeAttribute('controls');

          media.querySelectorAll('[data-video-toggle]').forEach((button) => {
            this.addControlListener(button, 'click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.toggleVideo(video);
            });
          });

          this.addControlListener(media, 'click', (event) => {
            if (event.target.closest('[data-video-toolbar]') || event.target.closest('[data-video-toggle]')) {
              return;
            }
            this.toggleVideo(video);
          });

          const muteButton = media.querySelector('[data-video-mute]');
          this.addControlListener(muteButton, 'click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            video.muted = !video.muted;
            this.updateVideoState(video);
          });

          const progress = media.querySelector('[data-video-progress]');
          this.addControlListener(progress, 'input', () => this.seekVideo(video));
          this.addControlListener(progress, 'change', () => this.seekVideo(video));

          this.addControlListener(video, 'play', () => {
            this.pauseOtherVideos(video);
            this.updateVideoState(video);
          });
          this.addControlListener(video, 'pause', () => this.updateVideoState(video));
          this.addControlListener(video, 'ended', () => this.updateVideoState(video));
          this.addControlListener(video, 'timeupdate', () => this.updateVideoProgress(video));
          this.addControlListener(video, 'loadedmetadata', () => this.updateVideoProgress(video));
          this.addControlListener(video, 'durationchange', () => this.updateVideoProgress(video));
          this.addControlListener(video, 'volumechange', () => this.updateVideoState(video));

          this.updateVideoState(video);
        });
      }

      addControlListener(element, eventName, handler, options) {
        if (!element) return;
        element.addEventListener(eventName, handler, options);
        this.controlHandlers.push({ element, eventName, handler, options });
      }

      move(direction) {
        if (!this.track || !this.slides.length) return;

        const currentIndex = this.getClosestSlideIndex();
        const targetIndex = Math.min(Math.max(currentIndex + direction, 0), this.slides.length - 1);
        this.scrollToSlide(this.slides[targetIndex]);
      }

      getClosestSlideIndex() {
        if (!this.track || !this.slides.length) return 0;

        return this.slides.reduce((closestIndex, slide, index) => {
          const firstSlideOffset = this.slides[0].offsetLeft;
          const currentDistance = Math.abs(slide.offsetLeft - firstSlideOffset - this.track.scrollLeft);
          const closestDistance = Math.abs(
            this.slides[closestIndex].offsetLeft - firstSlideOffset - this.track.scrollLeft
          );
          return currentDistance < closestDistance ? index : closestIndex;
        }, 0);
      }

      scrollToSlide(slide) {
        if (!this.track || !slide || !this.slides.length) return;
        this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
      }

      updateNavigation() {
        if (!this.track) return;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }

      toggleVideo(video) {
        if (!video) return;

        if (video.paused || video.ended) {
          if (video.ended) video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => this.updateVideoState(video));
        } else {
          video.pause();
        }
      }

      seekVideo(video) {
        const media = video.closest('.lp-primer-video-reviews__media');
        const progress = media?.querySelector('[data-video-progress]');
        if (!progress || !Number.isFinite(video.duration) || video.duration <= 0) return;

        video.currentTime = (Number(progress.value) / 100) * video.duration;
        this.updateVideoProgress(video);
      }

      pauseOtherVideos(activeVideo) {
        this.querySelectorAll('video').forEach((video) => {
          if (video !== activeVideo && !video.paused) video.pause();
        });
      }

      updateVideoState(video) {
        const media = video.closest('.lp-primer-video-reviews__media');
        if (!media) return;

        const isPlaying = !video.paused && !video.ended;
        const isMuted = video.muted || video.volume === 0;
        const label =
          media.querySelector('[data-video-toggle]')?.getAttribute('aria-label')?.replace(/^(Reproduzir|Pausar) /, '') ||
          'vídeo';

        media.classList.toggle('is-playing', isPlaying);
        media.classList.toggle('is-muted', isMuted);

        media.querySelectorAll('[data-video-toggle]').forEach((button) => {
          button.setAttribute('aria-label', `${isPlaying ? 'Pausar' : 'Reproduzir'} ${label}`);
        });

        const muteButton = media.querySelector('[data-video-mute]');
        muteButton?.setAttribute('aria-label', `${isMuted ? 'Ativar som de' : 'Silenciar'} ${label}`);

        this.updateVideoProgress(video);
      }

      updateVideoProgress(video) {
        const media = video.closest('.lp-primer-video-reviews__media');
        const progress = media?.querySelector('[data-video-progress]');
        const time = media?.querySelector('[data-video-time]');
        if (!progress || !time) return;

        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const percentage = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

        progress.value = percentage.toFixed(2);
        progress.style.setProperty('--progress', `${percentage}%`);
        progress.setAttribute('aria-valuetext', `${this.formatTime(currentTime)} de ${this.formatTime(duration)}`);
        time.textContent = this.formatTime(currentTime);
      }

      formatTime(seconds) {
        if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
        return `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;
      }

      handleBlockSelect(event) {
        const slide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        this.scrollToSlide(slide);
      }
    }
  );
}
